import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiScissors, FiDroplet, FiRefreshCw, FiStar, FiHeart } = FiIcons;

const Services = () => {
  React.useEffect(() => {
    document.title = "Our Signature Services at Melissa's Hairdressing Salon";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Explore the range of services at Melissa's Hairdressing Salon. Custom haircuts, professional coloring, and event styling await!";
    }
  }, []);

  const services = [
    {
      icon: FiScissors,
      title: "Haircuts",
      price: "From $45",
      image: "https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      description: "Transform your look with our expert precision cutting techniques. Our stylists specialize in creating cuts that complement your face shape, hair texture, and lifestyle.",
      features: [
        "Consultation and hair analysis",
        "Precision cutting techniques",
        "Face shape analysis",
        "Styling tips and maintenance advice",
        "Complimentary wash and blow-dry"
      ]
    },
    {
      icon: FiDroplet,
      title: "Hair Coloring",
      price: "From $85",
      image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      description: "Express your personality with our professional coloring services. From subtle highlights to bold transformations, we use premium products for vibrant, long-lasting results.",
      features: [
        "Color consultation and strand testing",
        "Premium color products",
        "Root touch-ups and full color",
        "Color correction services",
        "Aftercare treatment and advice"
      ]
    },
    {
      icon: FiStar,
      title: "Foils & Highlights",
      price: "From $95",
      image: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      description: "Add dimension and brightness to your hair with our expert foiling techniques. Create natural-looking highlights or dramatic contrasts with precision placement.",
      features: [
        "Balayage and traditional foiling",
        "Lowlights and highlights",
        "Partial or full head options",
        "Toning and gloss treatments",
        "Custom color matching"
      ]
    },
    {
      icon: FiRefreshCw,
      title: "Perms & Texturing",
      price: "From $75",
      image: "https://images.unsplash.com/photo-1559599101-f09722fb4948?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      description: "Add body, waves, or curls to your hair with our modern perming techniques. From loose beach waves to tight spirals, we create the texture you desire.",
      features: [
        "Digital and traditional perms",
        "Beach wave treatments",
        "Volume perms for fine hair",
        "Curl reformation services",
        "Texture analysis and consultation"
      ]
    },
    {
      icon: FiHeart,
      title: "Hair Wash & Treatments",
      price: "From $35",
      image: "https://images.unsplash.com/photo-1522338242992-e1a54906a8da?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      description: "Pamper your hair with our luxurious wash and treatment services. Deep conditioning, scalp treatments, and therapeutic hair masks for healthy, beautiful hair.",
      features: [
        "Relaxing scalp massage",
        "Deep conditioning treatments",
        "Protein and moisture therapy",
        "Scalp health analysis",
        "Aromatherapy wash experience"
      ]
    }
  ];

  const whyChooseUs = [
    {
      title: "Expertise & Experience",
      description: "Decades of combined experience guaranteeing high-quality services."
    },
    {
      title: "Personalized Care",
      description: "Every session is customized with your desires and needs in mind."
    },
    {
      title: "Relaxing Environment",
      description: "Enjoy a serene atmosphere while we work on your new look."
    },
    {
      title: "Premium Products",
      description: "We use only the finest professional-grade products for optimal results."
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Our Services at <span className="text-gradient">Melissa's</span><br />
              Hairdressing Salon
            </h1>
            <h2 className="font-playfair text-2xl md:text-3xl font-semibold text-gray-700 mb-6">
              Transforming Beauty, One Style at a Time
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              At Melissa's Hairdressing Salon, our range of services is designed to meet the diverse needs of our clients, ensuring each individual is thrilled with their look and feel.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Signature Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Each service is tailored to enhance your natural beauty and boost your confidence.
            </p>
          </motion.div>

          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-xl w-full h-80 object-cover"
                  />
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-3 rounded-full w-14 h-14 mb-6 flex items-center justify-center">
                    <SafeIcon icon={service.icon} className="text-white text-2xl" />
                  </div>
                  
                  <h3 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {service.title}
                  </h3>
                  
                  <p className="text-2xl font-semibold text-amber-600 mb-6">
                    {service.price}
                  </p>
                  
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-gray-600">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Link 
                    to="/contact-form"
                    className="btn-primary text-white px-8 py-3 rounded-full font-medium text-lg inline-block"
                  >
                    Book This Service
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Choose Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We're committed to providing exceptional service and results that exceed your expectations.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {whyChooseUs.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg card-hover"
              >
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-4">
                  {reason.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {reason.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to elevate your style?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Book your appointment today and let our expert team create the perfect look for you.
            </p>
            <Link 
              to="/contact-form"
              className="bg-white text-amber-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all inline-block"
            >
              Get in Touch with Us Today!
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;